/**
 * useMLSafe — Malayalam-safe rendering utilities for React components.
 *
 * PROBLEM: Tailwind's tracking-* classes inherit letter-spacing, which
 * breaks Malayalam grapheme clusters. Components use this hook to detect
 * Malayalam content and apply safe inline styles that override any
 * inherited letter-spacing/text-transform.
 */
import { useMemo } from 'react';

const ML_RANGE = /[\u0D00-\u0D7F]/;

/**
 * Returns true if the string contains any Malayalam Unicode character.
 */
export const hasMalayalam = (text) => {
  if (!text || typeof text !== 'string') return false;
  return ML_RANGE.test(text);
};

/**
 * Returns inline styles that are safe for Malayalam text.
 * Apply to any element that might render Malayalam.
 *
 * Critical properties:
 * - letterSpacing: 0  → prevents combining mark detachment
 * - textTransform: none → prevents uppercase corruption
 * - fontFeatureSettings: normal → lets the shaping engine work freely
 * - lineHeight: 1.8  → enough vertical clearance for vowel signs
 */
export const ML_SAFE_STYLE = {
  letterSpacing: 0,
  textTransform: 'none',
  fontFeatureSettings: 'normal',
  lineHeight: 1.8,
  unicodeBidi: 'plaintext',
  fontFamily: "'DM Sans', 'Noto Sans Malayalam', 'Manjari', system-ui, sans-serif",
};

/**
 * Returns ML_SAFE_STYLE only when the text contains Malayalam.
 * Returns an empty object for pure-English text (no performance impact).
 *
 * Usage:
 *   <p style={mlSafeStyle(synopsis)}>{synopsis}</p>
 */
export const mlSafeStyle = (text) =>
  hasMalayalam(text) ? ML_SAFE_STYLE : {};

/**
 * Returns ML_SAFE_STYLE unconditionally (use for containers that
 * ALWAYS display Malayalam content).
 */
export const mlStyle = () => ML_SAFE_STYLE;

/**
 * React hook: returns whether a text value contains Malayalam.
 * Memoized — recomputes only when text changes.
 *
 * Usage:
 *   const isML = useMalayalamText(synopsis);
 *   <p style={isML ? ML_SAFE_STYLE : {}}>{synopsis}</p>
 */
export const useMalayalamText = (text) =>
  useMemo(() => hasMalayalam(text), [text]);
